// ============================================================================
// ADD-ON SERVICE
// ============================================================================
// API client for the Add-on master module. Mirrors the MealPlan service pattern
// (server-side pagination, PagedResult -> PaginatedResponse transform, code+name
// uniqueness validation).

import { get, post, put, del } from '../client/axiosClient';
import { addonEndpoints } from '../endpoints/addon.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Addon,
  CreateAddonRequest,
  UpdateAddonRequest,
  AddonSearchRequest,
} from '../types/addon.types';

class AddonService {
  async getAll(params?: AddonSearchRequest): Promise<PaginatedResponse<Addon>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Addon', params)
      : addonEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Addon>> {
    const response = await get<ApiResponse<Addon>>(addonEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string, propertyId?: string): Promise<ApiResponse<Addon>> {
    let endpoint = addonEndpoints.getByCode(code);
    if (propertyId) endpoint = `${endpoint}?propertyId=${propertyId}`;
    const response = await get<ApiResponse<Addon>>(endpoint);
    return response.data;
  }

  async create(data: CreateAddonRequest): Promise<ApiResponse<Addon>> {
    const response = await post<ApiResponse<Addon>>(addonEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateAddonRequest): Promise<ApiResponse<Addon>> {
    const response = await put<ApiResponse<Addon>>(addonEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(addonEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  async validateCode(code: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = addonEndpoints.validateCode(code);
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) endpoint = `${endpoint}?${queryParts.join('&')}`;
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }

  async validateName(name: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = addonEndpoints.validateName(encodeURIComponent(name));
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) endpoint = `${endpoint}?${queryParts.join('&')}`;
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const addonService = new AddonService();
export default addonService;
